import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactSelect from "react-select";
import { useTable, useSortBy } from "react-table";
import swal from "sweetalert";
import { BACKEND_URL } from "../../baseUrl";
import {
  addAssesmentTemplate,
  deleteAssesmentTemplate,
  editAssesmentTemplate,
  getAssesmentsTemplates,
} from "../../services/AssesmentTemplateService";
import PageTitle from "../layouts/PageTitle";

const resolveImageUrl = (image) => {
  if (!image) return null;
  if (/^(https?:)?\/\//i.test(image) || image.startsWith("/")) return image;
  return `${BACKEND_URL}/public/${image}`;
};

const ROLE_OPTIONS = [
  { value: "Role 2", label: "Role 2" },
  { value: "Role 3", label: "Role 3" },
  { value: "Role 4", label: "Role 4" },
  { value: "Role 5", label: "Role 5" },
  { value: "Role 6", label: "Role 6" },
  { value: "admin", label: "Admin" },
];

const emptyForm = {
  ID: "",
  name: "",
  short: "",
  description: "",
  image: "",
  static_file: "",
  roles: [],
};

export const AssesmentTemplate = () => {
  const [assesmentTemplate, setAssesmentTemplate] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const getTemplates = () => {
    getAssesmentsTemplates().then((res) => {
      setAssesmentTemplate(res);
    });
  };

  React.useEffect(() => {
    getTemplates();
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id || row._id);
    setFormData({
      ID: row.ID || "",
      name: row.name || "",
      short: row.short || "",
      description: row.description || "",
      image: row.image || "",
      static_file: row.static_file || "",
      roles: Array.isArray(row.roles) ? row.roles : [],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.ID) {
      swal("Oops", "ID and Name are required", "error");
      return;
    }
    const payload = { ...formData };
    const action = editingId
      ? editAssesmentTemplate(editingId, payload)
      : addAssesmentTemplate(payload);
    action
      .then(() => {
        swal(
          "Good job!",
          editingId ? "Template updated" : "Template added",
          "success"
        );
        closeModal();
        getTemplates();
      })
      .catch((err) => {
        swal(
          err?.response?.data?.message || "Something went wrong",
          { icon: "error" }
        );
      });
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this template!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) return;
      deleteAssesmentTemplate(id)
        .then(() => {
          swal("Deleted!", { icon: "success" });
          getTemplates();
        })
        .catch((err) => {
          swal(
            err?.response?.data?.message || "Something went wrong",
            { icon: "error" }
          );
        });
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "ID",
        sortType: (a, b, columnId) => {
          const av = parseInt(a.values[columnId], 10);
          const bv = parseInt(b.values[columnId], 10);
          if (Number.isNaN(av) && Number.isNaN(bv)) return 0;
          if (Number.isNaN(av)) return 1;
          if (Number.isNaN(bv)) return -1;
          return av - bv;
        },
      },
      {
        Header: "Image",
        accessor: "image",
        disableSortBy: true,
        Cell: ({ value }) => {
          const src = resolveImageUrl(value);
          if (!src) return "";
          return (
            <img
              src={src}
              alt={value}
              style={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: 4,
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          );
        },
      },
      { Header: "Name", accessor: "name" },
      { Header: "Short", accessor: "short" },
      { Header: "Description", accessor: "description" },
      {
        Header: "Roles",
        accessor: "roles",
        disableSortBy: true,
        Cell: ({ value }) => {
          if (!Array.isArray(value) || value.length === 0)
            return <span className="text-muted">—</span>;
          return (
            <div className="d-flex flex-wrap" style={{ gap: 4 }}>
              {value.map((r) => (
                <span
                  key={r}
                  className="badge badge-primary"
                  style={{ fontSize: 11 }}
                >
                  {r}
                </span>
              ))}
            </div>
          );
        },
      },
      { Header: "Static File", accessor: "static_file" },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="d-flex">
            <Link
              className="btn btn-secondary shadow btn-xs sharp me-2"
              onClick={(e) => {
                e.preventDefault();
                openEditModal(row.original);
              }}
              to="#"
            >
              <i className="fas fa-pen"></i>
            </Link>
            <Link
              className="btn btn-danger shadow btn-xs sharp"
              onClick={(e) => {
                e.preventDefault();
                handleDelete(row.original.id || row.original._id);
              }}
              to="#"
            >
              <i className="fa fa-trash"></i>
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  const data = useMemo(() => [...assesmentTemplate], [assesmentTemplate]);
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { sortBy: [{ id: "ID", desc: false }] },
    },
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <>
      <PageTitle activeMenu="Assesment Templates" motherMenu="Assesment" />

      <Modal className="modal fade" show={showModal} onHide={closeModal}>
        <form onSubmit={handleSubmit}>
          <div className="modal-header">
            <h4 className="modal-title fs-20">
              {editingId ? "Edit Template" : "Add Template"}
            </h4>
            <button
              type="button"
              className="btn-close"
              onClick={closeModal}
              data-dismiss="modal"
            >
              <span></span>
            </button>
          </div>
          <div className="modal-body">
            <div className="add-contact-box">
              <div className="add-contact-content">
                <div className="form-group mb-3">
                  <label className="text-black font-w500">ID</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ID"
                    value={formData.ID}
                    onChange={handleChange}
                    placeholder="ID"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">Short</label>
                  <input
                    type="text"
                    className="form-control"
                    name="short"
                    value={formData.short}
                    onChange={handleChange}
                    placeholder="Short name"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Description"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">Image</label>
                  <input
                    type="text"
                    className="form-control"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="Image URL"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">Static File</label>
                  <input
                    type="text"
                    className="form-control"
                    name="static_file"
                    value={formData.static_file}
                    onChange={handleChange}
                    placeholder="Static file path"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">Roles</label>
                  <ReactSelect
                    isMulti
                    options={ROLE_OPTIONS}
                    value={ROLE_OPTIONS.filter((o) =>
                      formData.roles.includes(o.value)
                    )}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        roles: (selected || []).map((s) => s.value),
                      }))
                    }
                    placeholder="Select roles that should see this assessment"
                  />
                  <small className="text-muted">
                    Users with these roles will see this assessment
                    automatically.
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger light"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingId ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </Modal>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Assesment Templates</h4>
          <button className="btn btn-primary" onClick={openAddModal}>
            + New Template
          </button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <div className="dataTables_wrapper">
              {assesmentTemplate && assesmentTemplate.length > 0 ? (
                <table {...getTableProps()} className="table dataTable display">
                  <thead>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            {...column.getHeaderProps(
                              column.getSortByToggleProps()
                            )}
                          >
                            {column.render("Header")}
                            <span className="ml-1">
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <i className="fa fa-arrow-down" />
                                ) : (
                                  <i className="fa fa-arrow-up" />
                                )
                              ) : (
                                ""
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center">No Data Found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default AssesmentTemplate;
